import { useTheme } from './ThemeProvider';
import type { AuthComponentMap, ThemeComponentMap } from './types';

/**
 * Renders the active theme's implementation of a contract page/layout by name, wrapped in a
 * `.theme-<key>` scoping class. Themes that override CSS custom properties (colors, shadows,
 * etc. — see e.g. themes/glass-modern/tokens.css) scope those overrides under this class so
 * they never leak into other themes or into the fixed-tier (unthemed) admin pages, which never
 * render inside a ThemeSlot. See ADR Section 16.
 *
 * `scope="pages"` (default) resolves against the 12-key guest-marketing contract. `scope="auth"`
 * resolves against the pre-login Auth contract (ADR-2026-07-D) — auth necessarily follows the
 * platform-wide theme since a user's role/tier isn't known until after they authenticate.
 */
type ThemeSlotProps =
  | { page: keyof ThemeComponentMap; scope?: 'pages' }
  | { page: keyof AuthComponentMap; scope: 'auth' };

export function ThemeSlot({ page, scope = 'pages' }: ThemeSlotProps) {
  const { themeKey, components } = useTheme();
  const Component =
    scope === 'auth'
      ? components.auth[page as keyof AuthComponentMap]
      : components.pages[page as keyof ThemeComponentMap];
  return (
    <div className={`theme-${themeKey}`}>
      <Component />
    </div>
  );
}
